import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { ArrowUpward } from '@mui/icons-material';

interface Heading {
  id: string;
  text: string;
  level: number; // 2 or 3 for h2, h3
}

interface DocTableOfContentsProps {
  markdown: string;
}

export const DocTableOfContents: React.FC<DocTableOfContentsProps> = ({ markdown }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeId, setActiveId] = useState<string>('');

  // Extract headings from markdown
  useEffect(() => {
    const extractHeadings = () => {
      const headingRegex = /^(#{2,3})\s+(.+)$/gm;
      const extractedHeadings: Heading[] = [];
      let match;

      while ((match = headingRegex.exec(markdown)) !== null) {
        const level = match[1].length; // Number of # symbols
        const text = match[2].trim();
        // Generate ID from text (similar to how rehype-slug does it)
        const id = text
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-');

        extractedHeadings.push({ id, text, level });
      }

      setHeadings(extractedHeadings);
    };

    extractHeadings();
  }, [markdown]);

  // Track which heading is currently in view
  useEffect(() => {
    const handleScroll = () => {
      // Get all heading elements
      const headingElements = headings
        .map(h => document.getElementById(h.id))
        .filter((el): el is HTMLElement => el !== null);

      // Find the first heading that's below the viewport top
      let currentId = '';
      for (const el of headingElements) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= 100) {
          currentId = el.id;
        } else {
          break;
        }
      }

      setActiveId(currentId);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state

    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings]);

  const scrollToHeading = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const yOffset = -80; // Offset for fixed header
      const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Hide on mobile
  if (isMobile || headings.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: 280,
        flexShrink: 0,
        pl: 3,
        position: 'sticky',
        top: 80,
        maxHeight: 'calc(100vh - 100px)',
        overflow: 'auto'
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          border: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="subtitle2" fontWeight={600}>
            On This Page
          </Typography>
          <IconButton size="small" onClick={scrollToTop} title="Jump to top">
            <ArrowUpward fontSize="small" />
          </IconButton>
        </Box>

        {/* Table of Contents */}
        <List disablePadding>
          {headings.map((heading, index) => (
            <ListItem
              key={`${heading.id}-${index}`}
              disablePadding
              sx={{
                pl: heading.level === 3 ? 2 : 0
              }}
            >
              <ListItemButton
                onClick={() => scrollToHeading(heading.id)}
                selected={activeId === heading.id}
                sx={{
                  py: 0.75,
                  px: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    color: 'primary.main',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: 'primary.light'
                    }
                  }
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: heading.level === 3 ? '0.8125rem' : '0.875rem',
                    fontWeight: activeId === heading.id ? 600 : 400,
                    color: activeId === heading.id ? 'primary.main' : 'text.secondary',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {heading.text}
                </Typography>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};
